"use client";

import { useState } from "react";
import PhoneInput from "@/components/PhoneInput";
import CityPicker from "@/components/CityPicker";
import { SuggestInput } from "@/components/ui/SuggestInput";
import { useToast } from "@/components/ui/Toast";
import { useLocale } from "@/lib/locale-context";
import { track } from "@/lib/metrika";

// Витрина «Онлайн оплата» — по образцу /payment Солнышко, акцент синий.
// Отличие: единый сайт — сверху КАРУСЕЛЬ ГОРОДОВ; счёт создаётся в платёжном
// шлюзе ВЫБРАННОГО города (station + city_slug идут в /api/v1/payment/online).
const ACCENT = "#0039A6";

type CityOpt = { station: string; name: string; name_en?: string; slug: string; currency?: string };

interface Props {
  cities: CityOpt[];
  initialStation: string;
}

function Check({ checked, onToggle, children }: { checked: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 12, fontSize: 14, color: "#2B2F33", lineHeight: 1.45 }}>
      <button
        type="button"
        aria-pressed={checked}
        onClick={onToggle}
        style={{
          marginTop: 1, width: 18, height: 18, flexShrink: 0, cursor: "pointer",
          borderRadius: 5, display: "flex", alignItems: "center", justifyContent: "center",
          border: checked ? `1px solid ${ACCENT}` : "1px solid #C9D3E6",
          background: checked ? ACCENT : "#fff", transition: "all 0.15s",
        }}
      >
        {checked && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>}
      </button>
      <span style={{ cursor: "pointer" }} onClick={onToggle}>{children}</span>
    </div>
  );
}

export default function PaymentClient({ cities, initialStation }: Props) {
  const { t } = useLocale();
  const toast = useToast();
  const [station, setStation] = useState(initialStation);
  const sel = cities.find((c) => c.station === station);
  const citySlug = sel?.slug || "";

  const [car, setCar] = useState("");
  const [fio, setFio] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneDial, setPhoneDial] = useState("+7");
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [personal, setPersonal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fioFilter = (raw: string) => raw.replace(/[^A-Za-zА-Яа-яЁё\s\-.]/g, "");

  async function pay() {
    if (submitting) return;
    const sum = parseInt(amount, 10);
    if (!station) { toast.show(t("form.selectCity"), "error"); return; }
    if (!car.trim() || !fio.trim() || !phone.trim() || !email.trim() || !sum || sum <= 0) {
      toast.show(t("pay.fillAll"), "error");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.show(t("form.invalidEmail"), "error");
      return;
    }
    if (!privacy || !personal) {
      toast.show(t("consent.required"), "error");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch("/api/v1/payment/online", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          station, city_slug: citySlug,
          car, fio,
          phone: `${phoneDial} ${phone}`.trim(),
          email,
          amount: sum,
        }),
      });
      const data = await res.json().catch(() => null);
      if (data?.pay_url) {
        track("payment_submit", { city: citySlug || station });
        track("payment_redirect", { city: citySlug || station });
        window.location.href = data.pay_url;
        return;
      }
      toast.show(data?.error || t("pay.invoiceFail"), "error");
    } catch {
      toast.show(t("common.networkError"), "error");
    } finally {
      setSubmitting(false);
    }
  }

  const inputStyle: React.CSSProperties = {
    color: "#000", fontSize: 15, background: "none", borderRadius: 10,
    border: "2px solid #EDEDED", padding: 12, width: "100%", boxSizing: "border-box", fontFamily: "inherit",
  };

  return (
    <div style={{ maxWidth: 900, margin: "0 auto", padding: "48px 20px 80px" }}>
      <h1 style={{ fontSize: 36, fontWeight: "bold", color: "#000", marginBottom: 16 }}>{t("pay.h1")}</h1>
      <p style={{ color: "#000", fontSize: 16, marginBottom: 24, lineHeight: 1.5 }}>
        <strong style={{ color: "#d52b1e" }}>{t("pay.attention")}</strong> {t("pay.allRequired")}
      </p>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))", gap: 16, marginBottom: 16 }}>
        {/* Город — кастомный выпадающий список; счёт уйдёт в шлюз этого города */}
        <div style={{ gridColumn: "1 / -1" }}>
          <Field label={t("form.city")} hint={t("pay.cityHint")}>
            <CityPicker cities={cities} station={station} onPick={(st) => setStation(st)} inputStyle={inputStyle} />
          </Field>
        </div>
        <Field label={t("pay.car")} hint={t("pay.carHint")}>
          <input type="text" value={car} onChange={(e) => setCar(e.target.value)} style={inputStyle} placeholder="Kia Rio" />
        </Field>
        <Field label={t("documents.fio")} hint={t("pay.fioHint")}>
          <SuggestInput kind="fio" value={fio} onChange={setFio} filter={fioFilter} placeholder={t("documents.fioPlaceholder")} inputStyle={inputStyle} />
        </Field>
        <Field label={t("form.phone")}>
          <PhoneInput value={phone} onChange={(p, d) => { setPhone(p); setPhoneDial(d); }} height={52} />
        </Field>
        <Field label="Email">
          <SuggestInput kind="email" value={email} onChange={setEmail} placeholder="example@gmail.com" inputStyle={inputStyle} />
        </Field>
        <div style={{ gridColumn: "1 / -1" }}>
          <Field label={t("pay.amount")} hint={sel?.currency === "$" ? t("pay.amountHintUsd") : t("pay.amountHintRub")}>
            <input type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} style={inputStyle} placeholder="15000" />
          </Field>
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16, marginBottom: 28 }}>
        <Check checked={privacy} onToggle={() => setPrivacy(!privacy)}>
          {t("consent.privacyPrefix")}{" "}
          <a href="/legal/privacy" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: ACCENT, textDecoration: "underline" }}>{t("consent.privacy")}</a>
        </Check>
        <Check checked={personal} onToggle={() => setPersonal(!personal)}>
          {t("consent.personalPrefix")}{" "}
          <a href="/legal/personal-data" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ color: ACCENT, textDecoration: "underline" }}>{t("consent.personalData")}</a>
        </Check>
      </div>

      <button
        onClick={pay}
        disabled={submitting}
        style={{
          background: ACCENT, color: "#fff", border: "none", borderRadius: 10,
          padding: "18px 0", width: "100%", fontSize: 17, fontWeight: "bold",
          cursor: submitting ? "default" : "pointer", fontFamily: "inherit",
          opacity: submitting ? 0.6 : 1,
        }}
      >
        {submitting ? t("pay.creating") : t("pay.payBtn")}
      </button>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      <span style={{ color: "#000", fontSize: 14, fontWeight: "bold" }}>{label}</span>
      {children}
      {hint && <span style={{ color: "#8E8E93", fontSize: 12 }}>{hint}</span>}
    </div>
  );
}
